import { useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExperimentTabs } from "./ExperimentTabs";
import { ProviderEchoTable } from "./ProviderEchoTable";
import {
  fetchProviderEchoes,
  fetchProviderSummary,
  type ProviderFilters,
} from "./providerExperimentClient";

function initialDates() {
  const until = new Date();
  return {
    since: new Date(until.getTime() - 30 * 86400000).toISOString(),
    until: until.toISOString(),
  };
}

export function H3LocalPage() {
  const [filters, setFilters] = useState<ProviderFilters>(() => ({
    ...initialDates(),
    scope: "customer",
    variant: "all",
  }));
  const echoes = useInfiniteQuery({
    queryKey: ["h3-provider-echoes", filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      fetchProviderEchoes(filters, pageParam, signal),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    staleTime: 30_000,
  });
  const summary = useQuery({
    queryKey: ["h3-provider-summary", filters],
    queryFn: ({ signal }) => fetchProviderSummary(filters, signal),
    enabled: filters.scope === "customer",
    staleTime: 30_000,
  });
  const rows = [
    ...new Map(
      echoes.data?.pages
        .flatMap((page) => page.data)
        .map((row) => [row.workload_id, row]) ?? [],
    ).values(),
  ];
  const refresh = () => {
    const now = new Date().toISOString();
    if (filters.until.slice(0, 10) === now.slice(0, 10)) {
      setFilters({ ...filters, until: now });
    } else {
      void echoes.refetch();
      if (filters.scope === "customer") void summary.refetch();
    }
  };
  return (
    <div className="space-y-5 p-6">
      <ExperimentTabs />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">H3 providers</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            B2C WhatsApp model comparison. Assignment is stable per user; actual
            H3 clips can run on RunPod or a local GPU. Customer stars come from
            delivered Echoes.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/gpu-fleet">GPU Fleet</Link>
          </Button>
          <Button variant="outline" onClick={refresh}>
            Refresh
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <label className="grid gap-1 text-sm">
          Traffic
          <select
            className="rounded border bg-background p-2"
            value={filters.scope}
            onChange={(event) =>
              setFilters({
                ...filters,
                scope: event.target.value,
                variant: "all",
              })
            }
          >
            {[
              ["customer", "Customer A/B"],
              ["launch", "Guaranteed first five"],
              ["internal", "Internal grants"],
              ["excluded", "Other H3 routes"],
              ["historical", "Historical H3"],
              ["all", "All H3 Echoes"],
            ].map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          Variant
          <select
            className="rounded border bg-background p-2"
            value={filters.variant}
            onChange={(event) =>
              setFilters({ ...filters, variant: event.target.value })
            }
          >
            <option value="all">All</option>
            <option value="h3_local">H3 cohort</option>
            {filters.scope === "customer" && (
              <option value="veo">Veo control</option>
            )}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          Since
          <input
            type="date"
            className="rounded border bg-background p-2"
            value={filters.since.slice(0, 10)}
            onChange={(event) => {
              if (event.target.value)
                setFilters({
                  ...filters,
                  since: `${event.target.value}T00:00:00Z`,
                });
            }}
          />
        </label>
        <label className="grid gap-1 text-sm">
          Through
          <input
            type="date"
            className="rounded border bg-background p-2"
            value={filters.until.slice(0, 10)}
            onChange={(event) => {
              if (event.target.value)
                setFilters({
                  ...filters,
                  until: `${event.target.value}T23:59:59Z`,
                });
            }}
          />
        </label>
      </div>
      {(echoes.error || (filters.scope === "customer" && summary.error)) && (
        <p role="alert" className="text-destructive">
          {String(echoes.error || summary.error)}
        </p>
      )}
      {filters.scope === "customer" && summary.data && (
        <>
          {summary.data.truncated && (
            <p role="alert">
              Partial results: narrow the date range before comparing outcomes.
            </p>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            {summary.data.variants.map((metrics) => (
              <Card key={metrics.variant}>
                <CardHeader>
                  <CardTitle>
                    {metrics.variant === "h3_local"
                      ? "H3 cohort"
                      : "Veo control"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p>
                    <strong>{metrics.assigned_users}</strong> assigned users ·{" "}
                    {metrics.echoes} Echoes
                  </p>
                  <p>
                    {metrics.exposed_echoes} reached composition ·{" "}
                    {metrics.generated_echoes} generated ·{" "}
                    {metrics.delivered_echoes} delivered ·{" "}
                    {metrics.failed_echoes} failed
                  </p>
                  <p>
                    <strong>
                      {metrics.mean_rating?.toFixed(2) ?? "—"} / 5
                    </strong>{" "}
                    from {metrics.rated_echoes} rated Echoes (
                    {metrics.rated_users} users)
                  </p>
                  <p>
                    {metrics.rating_response_rate == null
                      ? "—"
                      : `${(metrics.rating_response_rate * 100).toFixed(1)}%`}{" "}
                    rating response ·{" "}
                    {metrics.mean_user_rating?.toFixed(2) ?? "—"} average per
                    user
                  </p>
                  <p>
                    {Object.entries(metrics.rating_distribution)
                      .map(([star, count]) => `${star}★: ${count}`)
                      .join(" · ")}
                  </p>
                  <p>
                    {metrics.h3_scenes}/{metrics.generated_scenes} H3 clips ·{" "}
                    {metrics.fallback_scenes} fallback · {metrics.static_scenes}{" "}
                    static
                  </p>
                  {metrics.missing_manifest_echoes > 0 && (
                    <p className="text-muted-foreground">
                      {metrics.missing_manifest_echoes} Echoes awaiting
                      selected-clip evidence.
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
      {filters.scope !== "customer" && (
        <p className="text-sm text-muted-foreground">
          These Echoes are observational evidence and are excluded from the
          randomized customer comparison.
        </p>
      )}
      {echoes.isPending ? (
        <p role="status">Loading Echoes…</p>
      ) : rows.length ? (
        <ProviderEchoTable echoes={rows} />
      ) : (
        !echoes.error && <p>No Echoes match these filters.</p>
      )}
      {echoes.hasNextPage && (
        <Button
          variant="outline"
          disabled={echoes.isFetchingNextPage}
          onClick={() => void echoes.fetchNextPage()}
        >
          {echoes.isFetchingNextPage ? "Loading…" : "Load more"}
        </Button>
      )}
    </div>
  );
}
H3LocalPage.path = "/experiments/h3-local";
